import { Layout } from '@/components/Layout';
import { IMAGES } from '@/assets/images';
import { SEO } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Scale, FileCheck, Clock, Award, Users, CheckCircle2, ArrowRight, Shield } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ROUTE_PATHS } from '@/lib/index';

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { type: 'spring', stiffness: 300, damping: 35 },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const staggerItem = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
};

export default function ForLawyers() {
  const collaborationReasons = [
    {
      icon: Award,
      titre: 'Crédibilité professionnelle reconnue',
      description: 'Expertise spécialisée en situations familiales complexes, haut conflit et dynamiques de coparentalité difficile.',
    },
    {
      icon: FileCheck,
      titre: 'Rigueur méthodologique',
      description: 'Évaluations conformes aux standards professionnels et lignes directrices de l\'Association of Family and Conciliation Courts.',
    },
    {
      icon: Clock,
      titre: 'Respect des délais',
      description: 'Engagement à respecter les échéanciers judiciaires avec une communication proactive sur l\'avancement des dossiers.',
    },
    {
      icon: Scale,
      titre: 'Qualité des rapports',
      description: 'Rapports détaillés, structurés et objectifs, fournissant des recommandations claires et bien fondées pour le tribunal.',
    },
    {
      icon: Users,
      titre: 'Collaboration efficace',
      description: 'Disponibilité pour échanger sur les particularités des dossiers et faciliter la coordination avec les équipes juridiques.',
    },
    {
      icon: Shield,
      titre: 'Éthique et confidentialité',
      description: 'Respect rigoureux des obligations déontologiques, de la confidentialité des informations et des règles d\'indépendance professionnelle propres à l\'expertise judiciaire.',
    },
  ];

  const processusSteps = [
    {
      numero: 1,
      titre: 'Contact initial',
      description: 'Prise de contact par l\'avocat avec les informations de base sur le mandat et les parties impliquées.',
    },
    {
      numero: 2,
      titre: 'Évaluation du mandat',
      description: 'Analyse des documents judiciaires, vérification des conflits d\'intérêts et évaluation de la faisabilité du mandat.',
    },
    {
      numero: 3,
      titre: 'Acceptation et calendrier',
      description: 'Confirmation formelle de l\'acceptation du mandat, établissement du calendrier et communication des honoraires.',
    },
    {
      numero: 4,
      titre: 'Réalisation de l\'expertise',
      description: 'Collecte d\'information, rencontres avec les parties, évaluation approfondie et analyse clinique rigoureuse.',
    },
    {
      numero: 5,
      titre: 'Rapport et suivi',
      description: 'Rédaction et dépôt du rapport d\'expertise, disponibilité pour témoignage ou clarifications si requis par le tribunal.',
    },
  ];

  const servicesDetails = [
    {
      titre: 'Expertise psychosociale complète',
      description: 'Évaluation approfondie des capacités parentales, du système familial et des besoins de l\'enfant dans un contexte de litige concernant la garde, les droits d\'accès ou l\'exercice de l\'autorité parentale.',
      points: [
        'Évaluation de la capacité parentale de chaque parent',
        'Analyse du système familial et des dynamiques relationnelles',
        'Identification des facteurs de risque et de protection',
        'Recommandations objectives pour le tribunal',
      ],
    },
    {
      titre: 'Expertise en situations complexes',
      description: 'Intervention spécialisée dans les dossiers de haut conflit, allégations d\'aliénation parentale, rupture de lien parent-enfant ou préoccupations pour la sécurité de l\'enfant.',
      points: [
        'Analyse des dynamiques de conflit élevé',
        'Évaluation des allégations et contre-allégations',
        'Expertise en refus de contact et rupture relationnelle',
        'Recommandations adaptées aux situations complexes',
      ],
    },
    {
      titre: 'Délais et processus',
      description: 'Engagement à respecter les échéanciers judiciaires tout en maintenant la rigueur et la qualité de l\'évaluation professionnelle.',
      points: [
        'Durée typique: 8 à 12 semaines selon la complexité',
        'Communication régulière sur l\'avancement du dossier',
        'Flexibilité pour accommoder les urgences judiciaires',
        'Disponibilité pour témoignage au tribunal',
      ],
    },
  ];

  return (
    <Layout>
      <SEO
        title="Pour les avocats — Expertise psychosociale et collaboration juridique"
        description="Collaboration avec les avocats en droit familial : rapports d'expertise psychosociale conformes aux standards judiciaires, disponibilité pour témoignage, délais respectés à Gatineau et Outaouais."
        keywords="expertise psychosociale avocats Gatineau, rapport expertise familiale tribunal, droit familial Outaouais, capacité parentale évaluation, témoignage expert"
        canonical="/pour-les-avocats"
      />
      <div className="min-h-screen">
        <section className="relative py-24 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src={IMAGES.CONSULTATION_7}
              alt="Consultation professionnelle"
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/60 to-background" />
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              className="max-w-4xl mx-auto text-center"
              initial="initial"
              animate="animate"
              variants={fadeInUp}
            >
              <Badge className="mb-6 text-base px-4 py-2">
                Pour les professionnels du droit
              </Badge>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 tracking-tight">
                Partenaire de confiance pour vos mandats d'expertise
              </h1>
              <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                Une expertise psychosociale rigoureuse et crédible pour éclairer les décisions du tribunal dans l'intérêt supérieur de l'enfant.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" asChild className="text-lg px-8">
                  <Link to={ROUTE_PATHS.CONTACT}>Discuter d'un mandat</Link>
                </Button>
                <Button size="lg" variant="outline" asChild className="text-lg px-8">
                  <a href="#processus">Processus de référence</a>
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              className="text-center mb-16"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <h2 className="text-4xl font-bold mb-4">Pourquoi collaborer avec nous</h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Une approche professionnelle qui allie rigueur clinique, respect des standards juridiques et engagement envers la qualité.
              </p>
            </motion.div>

            <motion.div
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              {collaborationReasons.map((reason, index) => {
                const Icon = reason.icon;
                return (
                  <motion.div key={index} variants={staggerItem}>
                    <Card className="h-full hover:shadow-lg transition-shadow duration-200">
                      <CardContent className="p-8">
                        <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center mb-6">
                          <Icon className="w-7 h-7 text-primary" />
                        </div>
                        <h3 className="text-xl font-semibold mb-3">{reason.titre}</h3>
                        <p className="text-muted-foreground leading-relaxed">{reason.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </section>

        <section className="py-24">
          <div className="container mx-auto px-4">
            <motion.div
              className="text-center mb-16"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <h2 className="text-4xl font-bold mb-4">Services pour avocats</h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Des évaluations professionnelles adaptées aux besoins spécifiques de vos dossiers en droit de la famille.
              </p>
            </motion.div>

            <motion.div
              className="grid lg:grid-cols-3 gap-8 max-w-7xl mx-auto"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              {servicesDetails.map((service, index) => (
                <motion.div key={index} variants={staggerItem}>
                  <Card className="h-full">
                    <CardContent className="p-8">
                      <h3 className="text-2xl font-semibold mb-4">{service.titre}</h3>
                      <p className="text-muted-foreground mb-6 leading-relaxed">{service.description}</p>
                      <ul className="space-y-3">
                        {service.points.map((point, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                            <span className="text-sm">{point}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        <section id="processus" className="py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              className="text-center mb-16"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <h2 className="text-4xl font-bold mb-4">Processus de référence</h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Un processus clair et structuré pour faciliter la collaboration et assurer la qualité de l'expertise.
              </p>
            </motion.div>

            <motion.div
              className="max-w-4xl mx-auto"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              {processusSteps.map((step, index) => (
                <motion.div
                  key={index}
                  className="relative pl-8 pb-12 last:pb-0"
                  variants={staggerItem}
                >
                  {index < processusSteps.length - 1 && (
                    <div className="absolute left-[15px] top-12 bottom-0 w-0.5 bg-border" />
                  )}
                  <div className="absolute left-0 top-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold text-sm">
                    {step.numero}
                  </div>
                  <Card className="ml-4">
                    <CardContent className="p-6">
                      <h3 className="text-xl font-semibold mb-2">{step.titre}</h3>
                      <p className="text-muted-foreground leading-relaxed">{step.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        <section className="py-24">
          <div className="container mx-auto px-4">
            <motion.div
              className="max-w-4xl mx-auto"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <Card className="bg-primary text-primary-foreground">
                <CardContent className="p-12 text-center">
                  <h2 className="text-3xl font-bold mb-4">
                    Prêt à collaborer sur un mandat?
                  </h2>
                  <p className="text-lg mb-8 opacity-90">
                    Contactez-nous pour discuter des particularités de votre dossier et obtenir un calendrier préliminaire.
                  </p>
                  <Button
                    size="lg"
                    variant="secondary"
                    asChild
                    className="text-lg px-8"
                  >
                    <Link to={ROUTE_PATHS.CONTACT}>
                      Discuter d'un mandat
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
